export const QUIZ_BASE = "/get-ready/quiz";
export const KNOWLEDGE_HISTORY_BASE = "/get-ready/knowledge-history";

function normalizeSlugSegment(name) {
    if (!name) return "";
    let segment = String(name).trim().replace(/^class\s+/i, "");
    if (segment.toLowerCase() === "industrial machinery") {
        return "industrial-machinery";
    }
    return segment.toLowerCase().replace(/\s+/g, "");
}

export function toLicenseSlug(name) {
    return normalizeSlugSegment(name);
}

export function toSubLicenseSlug(name) {
    return normalizeSlugSegment(name);
}

export function findLicenseBySlug(slug, licenseTypes) {
    if (!slug || !licenseTypes?.length) return null;
    const target = String(slug).toLowerCase();
    return (
        licenseTypes.find(
            (item) => toLicenseSlug(item.LicenseTypeName) === target
        ) || null
    );
}

export function findSubLicenseBySlug(slug, subLicenseTypes) {
    if (!slug || !subLicenseTypes?.length) return null;
    const target = String(slug).toLowerCase();
    return (
        subLicenseTypes.find(
            (item) => toSubLicenseSlug(item.SubLicenseTypeName) === target
        ) || null
    );
}

export function quizHomePath() {
    return QUIZ_BASE;
}

export function quizLicenseTypePath(licenseTypeName) {
    return `${QUIZ_BASE}/${toLicenseSlug(licenseTypeName)}`;
}

export function quizLicenseTypeSelectPath(licenseTypeName) {
    return `${QUIZ_BASE}/${toLicenseSlug(licenseTypeName)}/select`;
}

export function quizSubLicenseSelectPath(licenseTypeName, subLicenseTypeName) {
    return `${QUIZ_BASE}/${toLicenseSlug(licenseTypeName)}/${toSubLicenseSlug(subLicenseTypeName)}/select`;
}

export function quizAttemptPath({
    licenseTypeName,
    subLicenseTypeName,
    questionCount,
}) {
    const licenseSlug = toLicenseSlug(licenseTypeName);
    if (subLicenseTypeName) {
        return `${QUIZ_BASE}/${licenseSlug}/${toSubLicenseSlug(subLicenseTypeName)}/${questionCount}`;
    }
    return `${QUIZ_BASE}/${licenseSlug}/${questionCount}`;
}

export function isQuizSlugRoute(pathname) {
    return pathname.includes(`${QUIZ_BASE}/`);
}

/** True when API course payload includes sub-licence steps (G1, G2, …). */
export function hasSubLicenseTypes(subLicenseTypes) {
    return Array.isArray(subLicenseTypes) && subLicenseTypes.length > 0;
}

export function licenseHasSubLicenseTypes(courses) {
    return hasSubLicenseTypes(courses?.SubLicenseTypes);
}

/** Profile code sent to API for licence types without a sub-licence step. */
export function toQuizProfileCode(licenseTypeName) {
    return String(licenseTypeName || "").trim().toUpperCase();
}

/** Display label for quiz header / selection screens. */
export function formatLicenseQuizLabel(licenseTypeName, subLicenseTypeName) {
    if (subLicenseTypeName) {
        const sub = String(subLicenseTypeName).trim();
        if (/^class\s+/i.test(sub)) return sub;
        return `Class ${sub}`;
    }
    const license = String(licenseTypeName || "").trim();
    if (!license) return "";
    if (license.toLowerCase() === "industrial machinery") return license;
    return `Class ${license}`;
}

export function quizHistoryLegacyResultPath(quizAttemptId) {
    return `${KNOWLEDGE_HISTORY_BASE}/${quizAttemptId}/results`;
}

export function isAttemptShortCode(value) {
    return /^QU[A-Za-z0-9]{4}$/.test(String(value || ""));
}

/** Resolve attempt id (QUxxxx or legacy UUID) from a knowledge-history results URL. */
export function parseKnowledgeHistoryAttemptId(pathname) {
    const prefix = `${KNOWLEDGE_HISTORY_BASE}/`;
    if (!pathname?.startsWith(prefix)) return null;

    const parts = pathname.slice(prefix.length).split("/").filter(Boolean);
    if (!parts.length) return null;

    if (parts.length === 2 && parts[1] === "results") {
        return parts[0];
    }

    const last = parts[parts.length - 1];
    return isAttemptShortCode(last) ? last : null;
}

export function quizHistoryResultPath({
    licenseTypeName,
    subLicenseTypeName,
    questionCount,
    attemptShortCode,
}) {
    const licenseSlug = toLicenseSlug(licenseTypeName);
    if (subLicenseTypeName) {
        return `${KNOWLEDGE_HISTORY_BASE}/${licenseSlug}/${toSubLicenseSlug(subLicenseTypeName)}/${questionCount}/${attemptShortCode}`;
    }
    return `${KNOWLEDGE_HISTORY_BASE}/${licenseSlug}/${questionCount}/${attemptShortCode}`;
}

export function buildQuizHistoryResultPath(row, licenseTypes = []) {
    const {
        attemptShortCode,
        licenseTypeId,
        subLicenseTypeId,
        scoreTotal,
        quizAttemptId,
    } = row;

    if (!attemptShortCode) {
        return quizHistoryLegacyResultPath(quizAttemptId);
    }

    const license = licenseTypes.find(
        (item) => String(item.LicenseTypeId) === String(licenseTypeId)
    );
    if (!license?.LicenseTypeName) {
        return quizHistoryLegacyResultPath(quizAttemptId);
    }

    let subLicenseTypeName;
    if (subLicenseTypeId) {
        const sub = license.SubLicenseTypes?.find(
            (item) =>
                String(item.SubLicenseTypeId) === String(subLicenseTypeId)
        );
        subLicenseTypeName = sub?.SubLicenseTypeName;
    }

    return quizHistoryResultPath({
        licenseTypeName: license.LicenseTypeName,
        subLicenseTypeName,
        questionCount: scoreTotal,
        attemptShortCode,
    });
}
